/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import Router from 'next/router';
import { ComponentProps } from 'react';

import { setTokenRevocation } from '../../api/auth/auth.requests';
import { useGlobalStore } from '../../stores/global/global.store';
import { Toast } from '../../ui-library/Patterns/Toast';
import { resetAnalyticsStore, sessionEndEvent } from '../../utils/analytics/session';
import { clearAuthToken, getAuthToken } from '../../utils/auth';

type NotificationParams = {
  notificationTitle?: string;
  notificationDescription?: string;
  notificationTheme?: ComponentProps<typeof Toast>['theme'];
};

type RestartSessionParams = {
  notificationTitle?: NotificationParams['notificationTitle'];
  notificationDescription?: NotificationParams['notificationDescription'];
  notificationTheme?: NotificationParams['notificationTheme'];
};

type ValidateSessionParams = {
  activeSession?: boolean;
  notificationTitle?: NotificationParams['notificationTitle'];
  notificationDescription?: NotificationParams['notificationDescription'];
};

export const removeUserSession = async (activeSession?: boolean) => {
  if (activeSession) {
    await sessionEndEvent();
    await setTokenRevocation();
  } else {
    resetAnalyticsStore();
    clearAuthToken();
  }
};

export const restartUserSession = async (params?: RestartSessionParams) => {
  const { setNotification } = useGlobalStore.getState();
  const { notificationTitle, notificationDescription, notificationTheme } = params || {};
  const notification = notificationTitle || notificationDescription;

  await Router.push('/');

  if (notification) {
    setNotification({
      display: true,
      title: notificationTitle,
      description: notificationDescription,
      theme: notificationTheme || 'error',
    });
  }
};

export const validateUserSession = async ({
  activeSession = true,
  notificationTitle,
  notificationDescription,
}: ValidateSessionParams): Promise<boolean> => {
  const authToken = getAuthToken();
  const hasToken = !!authToken?.token && activeSession;

  if (!hasToken) {
    await removeUserSession(false);
    await restartUserSession();
    return false;
  }

  const isTokenExpired = authToken.expirationDate ? Date.now() >= authToken.expirationDate - 60 * 1000 : true;
  const hasActiveToken = !isTokenExpired;

  if (!hasActiveToken) {
    await removeUserSession(activeSession);
    await restartUserSession({
      notificationTitle,
      notificationDescription,
      notificationTheme: 'error',
    });
    return false;
  }

  return hasActiveToken;
};
